import { zodResolver } from "@hookform/resolvers/zod";
import { Loader2, Plus, X } from "lucide-react";
import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";

import { BudgetFormWithComments } from "../../components/BudgetForm";

import {
  DebitFormData,
  useDebitSchema,
} from "@/components/forms/budgetPlanning/BudgetFormTypes";
import { Button } from "@/components/ui/v2/button";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/v2/dialog";
import {
  ClientBudgetProvider,
  DebitBudgetPayload,
} from "@/lib/contexts/ClientBudget/ClientBudgetProvider";
import { useClientBudget } from "@/lib/hooks/useClientBudget";
import { ClientType } from "@/types/client/ClientType";

interface DebitBudgetDialogProps {
  clientId: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess: () => void;
  client?: ClientType;
}

const DebitBudgetDialogContent = ({
  onOpenChange,
  onSuccess,
  client,
  clientId,
  open,
}: DebitBudgetDialogProps) => {
  const { t } = useTranslation();
  const { debitBudget } = useClientBudget();
  const { debitSchema } = useDebitSchema();
  const [canClose, setCanClose] = useState(true);
  const form = useForm<DebitFormData>({
    resolver: zodResolver(debitSchema),
    mode: "onChange",
  });

  const handleSubmit = (data: DebitFormData) => {
    const payload: DebitBudgetPayload = {
      clientId: clientId,
      payload: {
        amount: data.budget,
        comments: data?.comments || "-",
      },
    };

    debitBudget.mutate(payload.payload, {
      onSuccess: () => {
        onSuccess();
        onOpenChange(false);
        form.reset();
        form.clearErrors();
      },
    });
  };

  useEffect(() => {
    if (debitBudget.isPending) {
      setCanClose(false);
    } else {
      setCanClose(true);
    }
  }, [debitBudget.isPending]);

  useEffect(() => {
    if (!open) {
      form.reset();
      form.clearErrors();
    }
  }, [open, form]);

  const errorCode = debitBudget.error?.response?.data?.[0]?.errorCode;

  const handleOpenChange = (newOpen: boolean) => {
    if (!newOpen && canClose) {
      onOpenChange(false);
      form.reset();
      form.clearErrors();
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent
        showCloseButton={canClose}
        onInteractOutside={(e) => {
          if (!canClose) {
            e.preventDefault();
          }
        }}
        onEscapeKeyDown={(e) => {
          if (!canClose) {
            e.preventDefault();
          }
        }}
      >
        <DialogHeader>
          <DialogTitle>{client?.name}</DialogTitle>
        </DialogHeader>
        <BudgetFormWithComments
          label={`${t("common.debit")} ${t("common.budget")}`}
          methods={form}
          onSubmit={handleSubmit}
          error={errorCode && t(`errorCodes.${errorCode}`)}
        />
        <DialogFooter className="">
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={!canClose}
          >
            <X className="size-4" />
            {t("common.cancel")}
          </Button>
          <Button
            onClick={form.handleSubmit(handleSubmit)}
            disabled={debitBudget.isPending || !canClose}
          >
            {debitBudget.isPending ? (
              <Loader2 className="size-4 animate-spin" />
            ) : (
              <Plus className="size-4" />
            )}
            {t("actions.save", { entity: t("common.debit") })}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export const DebitBudgetDialogInner = (props: DebitBudgetDialogProps) => {
  return (
    <ClientBudgetProvider clientId={props.clientId}>
      <DebitBudgetDialogContent {...props} />
    </ClientBudgetProvider>
  );
};
